import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../components/PageHeader';
import { Row, Col } from '../components/Layout';
import { TermsOfServiceModal } from '../components/TermsOfServiceModal';
import { LanguageSelectModal } from '../components/LanguageSelectModal';
import { PrivacySecurityModal } from '../components/PrivacySecurityModal';
interface AccountPageProps {
  onNavigate: (page: string) => void;
}

const AccountPage: React.FC<AccountPageProps> = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const [toggles, setToggles] = useState(() => {
    try {
      const saved = localStorage.getItem('userPreferences');
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return { notifs: true, dm: false, location: true, online: true };
  });
  const [showTos, setShowTos] = useState(false);
  const [showLang, setShowLang] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const toggle = (k: keyof typeof toggles) => {
    setToggles((p: typeof toggles) => {
      const newToggles = { ...p, [k]: !p[k] };
      localStorage.setItem('userPreferences', JSON.stringify(newToggles));
      return newToggles;
    });
  };

  const handleDeleteAccount = () => {
    if (window.confirm(t('account.sure_delete', 'Emin misiniz? Tüm hesap verileriniz KESİN ve GERİ ALINAMAZ bir şekilde silinecektir!'))) {
      alert(t('account.delete_queued', 'Hesap silme işlemi yöneticiler tarafından incelenmek üzere kuyruğa alındı.'));
      logout();
    }
  };

  const handleLanguageSelect = () => {
    setShowLang(true);
  };

  const handleHelpCenter = () => {
    alert(t('account.help_prep', 'Yardım Merkezi hazırlanıyor. Şimdilik support@buddylive.com adresine destek talebi gönderebilirsiniz.'));
  };

  const handleExportData = () => {
    const userData = {
      username: user?.username,
      email: user?.email,
      bio: user?.bio,
      createdAt: user?.createdAt,
      coins: user?.coins,
      followers: user?.followers,
      following: user?.following
    };
    const dataStr = JSON.stringify(userData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', `buddy-live-data-${user?.username}.json`);
    linkElement.click();
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)', animation: 'slideR .4s ease' }}>
      <PageHeader title={t('account.title', 'Ayarlar')} onDrawer={() => { }} /> {/* Drawer logic managed by App.tsx override */}

      <div style={{ flex: 1, overflowY: 'auto', padding: '0 18px 40px' }}>

        {/* VIP Banner */}
        <div style={{ padding: '16px', background: 'linear-gradient(135deg,rgba(255,209,102,.15),rgba(255,138,61,.1))', borderRadius: 20, border: '1px solid rgba(255,209,102,.25)', marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Row style={{ gap: 12 }}>
            <div style={{ fontSize: 24 }}>👑</div>
            <Col>
              <div className="syne" style={{ fontSize: 16, fontWeight: 700, color: 'var(--gold)' }}>{t('account.premium', "Premium'a Geç")}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{t('account.premiumDesc', 'Reklamsız deneyim ve ekstra özellikler')}</div>
            </Col>
          </Row>
          <button onClick={() => onNavigate('coins')} style={{ background: 'var(--gold)', color: '#000', border: 'none', padding: '8px 16px', borderRadius: 12, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('account.review', 'İncele')}</button>
        </div>

        {/* Section: Account */}
        <div className="syne" style={{ fontSize: 14, fontWeight: 700, color: 'var(--muted)', marginBottom: 12, paddingLeft: 4 }}>{t('account.sections.account', 'HESAP')}</div>
        <div style={{ background: 'var(--surface)', borderRadius: 20, padding: '8px 16px', marginBottom: 24, border: '1px solid var(--border)' }}>
          {[
            { icon: '👤', label: t('account.items.editProfile', 'Profili Düzenle'), val: user?.username, action: () => onNavigate('profile') as (() => void) | undefined },
            { icon: '🔒', label: t('account.items.privacy', 'Gizlilik ve Güvenlik'), action: () => setShowPrivacy(true) },
            { icon: '💎', label: t('account.items.wallet', 'Cüzdan ve Bakiye'), val: t('account.diamonds', '{{count}} Elmas', { count: user?.coins || 0 }), color: 'var(--cyan)', action: () => onNavigate('coins') as (() => void) | undefined },
            { icon: '📉', label: t('account.items.downloadData', 'Verilerimi İndir'), action: handleExportData as (() => void) | undefined },
          ].map((item: any, i) => (
            <div key={i} onClick={item.action} style={{ display: 'flex', alignItems: 'center', padding: '16px 0', borderBottom: i === 3 ? 'none' : '1px solid var(--border)', justifyContent: 'space-between', cursor: item.action ? 'pointer' : 'default' }}>
              <Row style={{ gap: 12 }}>
                <div style={{ fontSize: 18 }}>{item.icon}</div>
                <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text)' }}>{item.label}</div>
              </Row>
              <Row style={{ gap: 10 }}>
                {item.val && <div style={{ fontSize: 13, color: item.color || 'var(--muted)', fontWeight: item.color ? 600 : 400 }}>{item.val}</div>}
                <div style={{ color: 'var(--muted)' }}>›</div>
              </Row>
            </div>
          ))}
        </div>

        {/* Section: Preferences */}
        <div className="syne" style={{ fontSize: 14, fontWeight: 700, color: 'var(--muted)', marginBottom: 12, paddingLeft: 4 }}>{t('account.sections.preferences', 'TERCİHLER')}</div>
        <div style={{ background: 'var(--surface)', borderRadius: 20, padding: '8px 16px', marginBottom: 24, border: '1px solid var(--border)' }}>
          {[
            { icon: '🔔', label: t('account.items.notifications', 'Bildirimler'), state: 'notifs' },
            { icon: '✉️', label: t('account.items.friendsOnly', 'Sadece Arkadaşlardan Mesaj'), state: 'dm' },
            { icon: '📍', label: t('account.items.location', 'Konumumu Göster'), state: 'location' },
            { icon: '🟢', label: t('account.items.onlineStatus', 'Çevrimiçi Görün'), state: 'online' },
          ].map((item, i) => (
            <Row key={i} style={{ padding: '16px 0', borderBottom: i === 3 ? 'none' : '1px solid var(--border)', justifyContent: 'space-between' }}>
              <Row style={{ gap: 12 }}>
                <div style={{ fontSize: 18 }}>{item.icon}</div>
                <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text)' }}>{item.label}</div>
              </Row>
              <div onClick={() => toggle(item.state as any)} style={{ width: 44, height: 24, borderRadius: 12, background: toggles[item.state as keyof typeof toggles] ? 'var(--green)' : 'var(--surface2)', position: 'relative', cursor: 'pointer', transition: 'all .3s' }}>
                <div style={{ width: 20, height: 20, background: '#fff', borderRadius: '50%', position: 'absolute', top: 2, left: toggles[item.state as keyof typeof toggles] ? 22 : 2, transition: 'all .3s', boxShadow: '0 2px 5px rgba(0,0,0,.2)' }} />
              </div>
            </Row>
          ))}
        </div>

        {/* Section: App */}
        <div className="syne" style={{ fontSize: 14, fontWeight: 700, color: 'var(--muted)', marginBottom: 12, paddingLeft: 4 }}>{t('account.sections.app', 'UYGULAMA')}</div>
        <div style={{ background: 'var(--surface)', borderRadius: 20, padding: '8px 16px', marginBottom: 32, border: '1px solid var(--border)' }}>
          {[
            { icon: '🌐', label: t('account.items.language', 'Dil'), val: i18n.language === 'tr' ? 'Türkçe' : i18n.language.toUpperCase(), action: handleLanguageSelect },
            { icon: '❓', label: t('account.items.helpCenter', 'Yardım Merkezi'), action: handleHelpCenter },
            { icon: '📜', label: t('account.items.terms', 'Hizmet Şartları'), action: () => setShowTos(true) },
            { icon: '🗑️', label: t('account.items.deleteAccount', 'Hesabı Sil'), color: '#FF5555', action: handleDeleteAccount }
          ].map((item: any, i) => (
            <div key={i} onClick={item.action} style={{ display: 'flex', alignItems: 'center', padding: '16px 0', borderBottom: i === 3 ? 'none' : '1px solid var(--border)', justifyContent: 'space-between', cursor: item.action ? 'pointer' : 'default' }}>
              <Row style={{ gap: 12 }}>
                <div style={{ fontSize: 18 }}>{item.icon}</div>
                <div style={{ fontSize: 15, fontWeight: item.color ? 600 : 500, color: item.color || 'var(--text)' }}>{item.label}</div>
              </Row>
              <Row style={{ gap: 10 }}>
                {item.val && <div style={{ fontSize: 13, color: 'var(--muted)' }}>{item.val}</div>}
                <div style={{ color: 'var(--muted)' }}>›</div>
              </Row>
            </div>
          ))}
        </div>

        <button onClick={() => {
          if (window.confirm(t('account.sure_logout', "Çıkış yapmak istediğinize emin misiniz?"))) {
            logout();
            window.location.reload();
          }
        }}
          style={{ width: '100%', background: 'rgba(255,92,92,.1)', border: '1px solid rgba(255,92,92,.2)', padding: 16, borderRadius: 16, color: '#FF5C5C', fontSize: 15, fontWeight: 700, fontFamily: 'Syne', cursor: 'pointer', marginBottom: 40 }}>
          {t('account.logout', 'Mevcut Oturumdan Çıkış Yap')}
        </button>
        <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--muted)', marginBottom: 20 }}>
          Buddy Live v2.0.0
        </div>
      </div>

      {/* Modals */}
      {showTos && <TermsOfServiceModal onClose={() => setShowTos(false)} />}
      {showLang && <LanguageSelectModal onClose={() => setShowLang(false)} />}
      {showPrivacy && <PrivacySecurityModal onClose={() => setShowPrivacy(false)} />}
    </div>
  );
};
export default AccountPage;
